import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart3, Activity, Users, Gamepad2, DollarSign, TrendingUp, TrendingDown,
  RefreshCw, Calendar, Download, Clock, Zap, Target, Award, PercentIcon,
  ArrowUpRight, ArrowDownRight, Eye, MousePointer, Timer, AlertTriangle
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { adminService } from '../services/adminService';
import './Metrics.css';

interface AnalyticsSummary {
  total_users: number;
  active_users_today: number;
  total_games: number;
  completed_games: number;
  total_revenue: number;
  total_payouts: number;
  platform_margin: number;
  completion_rate: number;
  avg_session_duration: number;
  new_users_today: number;
  ad_impressions_today: number;
  user_growth_percentage: number;
  revenue_growth_percentage: number;
  active_games_now?: number;
}

interface TimeSeriesData {
  date: string;
  users: number;
  sessions: number;
}

interface RevenueData {
  date: string;
  revenue: number;
}

interface AdPerformanceData {
  date: string;
  impressions: number;
  clicks: number;
}

interface GameModeData {
  [key: string]: string | number;
  name: string;
  value: number;
  color: string;
}

interface PerformanceKPIs {
  conversionRate: number;
  completionRate: number;
  retentionRate: number;
  adFillRate: number;
}

interface Insight {
  icon: 'good' | 'warning' | 'info';
  title: string;
  description: string;
}

const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
];

function parseDays(value: string): number {
  return parseInt(value, 10) || 7;
}

function formatDate(raw: string): string {
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return raw;
  }
}

function settled<T>(result: PromiseSettledResult<T>, fallback: T): T {
  return result.status === 'fulfilled' ? result.value : fallback;
}

const Metrics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('7');
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [adPerformanceData, setAdPerformanceData] = useState<AdPerformanceData[]>([]);
  const [gameModeData, setGameModeData] = useState<GameModeData[]>([]);
  const [performanceKPIs, setPerformanceKPIs] = useState<PerformanceKPIs>({
    conversionRate: 0,
    completionRate: 0,
    retentionRate: 0,
    adFillRate: 0,
  });
  const [insights, setInsights] = useState<Insight[]>([]);
  const lastUpdatedRef = useRef<Date>(new Date());

  const loadAnalytics = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      const days = parseDays(dateRange);

      const [
        summaryResult,
        dauResult,
        gameAnalyticsResult,
        houseMarginsResult,
        adImpressionsResult,
        expandedResult,
        engagementResult,
        adEventsResult,
      ] = await Promise.allSettled([
        adminService.getAnalyticsSummary(days),
        adminService.getDailyActiveUsers(days),
        adminService.getGameAnalytics(days),
        adminService.getHouseMargins(days),
        adminService.getAdImpressions(days),
        adminService.getExpandedAnalytics(days),
        adminService.getUserEngagement(days),
        adminService.getAdEventsSummary(days),
      ]);

      const summaryData = settled(summaryResult, null);
      const dauData = settled(dauResult, []);
      const gameData = settled(gameAnalyticsResult, []);
      const marginsData = settled(houseMarginsResult, []);
      const adData = settled(adImpressionsResult, []);
      const expandedData = settled(expandedResult, null);
      const engagementData = settled(engagementResult, null);
      const adEventsData = settled(adEventsResult, null);

      if (summaryData) {
        setSummary(summaryData);
      }

      // --- Time series: merge DAU + game sessions by date ---
      const dauArr = Array.isArray(dauData) ? dauData : [];
      const gameArr = Array.isArray(gameData) ? gameData : [];

      const dateMap = new Map<string, { users: number; sessions: number }>();
      for (const row of dauArr) {
        const key = row?.date || row?.day;
        if (!key) continue;
        const existing = dateMap.get(key) || { users: 0, sessions: 0 };
        existing.users = row?.active_users ?? row?.count ?? row?.users ?? 0;
        dateMap.set(key, existing);
      }
      for (const row of gameArr) {
        const key = row?.date || row?.day;
        if (!key) continue;
        const existing = dateMap.get(key) || { users: 0, sessions: 0 };
        existing.sessions = row?.total_sessions ?? row?.total_games ?? row?.sessions ?? row?.count ?? 0;
        dateMap.set(key, existing);
      }

      const sortedDates = Array.from(dateMap.entries())
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());
      setTimeSeriesData(
        sortedDates.map(([date, vals]) => ({
          date: formatDate(date),
          users: vals.users,
          sessions: vals.sessions,
        }))
      );

      // --- Protocol distribution from game analytics ---
      let demoTotal = 0;
      let multiplyTotal = 0;
      for (const row of gameArr) {
        demoTotal += row?.demo_games ?? row?.demo_sessions ?? 0;
        multiplyTotal += row?.multiply_games ?? row?.multiply_sessions ?? row?.real_games ?? 0;
      }
      const totalProtocol = demoTotal + multiplyTotal;
      if (totalProtocol > 0) {
        setGameModeData([
          { name: 'Demo Games', value: demoTotal, color: '#410C6D' },
          { name: 'Multiply Games', value: multiplyTotal, color: '#22c55e' },
        ]);
      } else {
        setGameModeData([
          { name: 'Demo Games', value: 0, color: '#410C6D' },
          { name: 'Multiply Games', value: 0, color: '#22c55e' },
        ]);
      }

      // --- Revenue trend from house margins ---
      const marginsArr = Array.isArray(marginsData) ? marginsData : [];
      setRevenueData(
        marginsArr
          .filter((r: any) => r?.date || r?.day)
          .sort((a: any, b: any) => new Date(a.date || a.day).getTime() - new Date(b.date || b.day).getTime())
          .map((r: any) => ({
            date: formatDate(r.date || r.day),
            revenue: r?.ad_revenue ?? r?.total_revenue ?? r?.revenue ?? r?.house_margin ?? 0,
          }))
      );

      // --- Ad performance from ad impressions or ad events ---
      const adArr = Array.isArray(adData) ? adData : [];
      if (adArr.length > 0) {
        setAdPerformanceData(
          adArr
            .filter((r: any) => r?.date || r?.day)
            .sort((a: any, b: any) => new Date(a.date || a.day).getTime() - new Date(b.date || b.day).getTime())
            .map((r: any) => ({
              date: formatDate(r.date || r.day),
              impressions: r?.impressions ?? r?.total_impressions ?? r?.count ?? 0,
              clicks: r?.clicks ?? r?.total_clicks ?? 0,
            }))
        );
      } else if (adEventsData && Array.isArray(adEventsData?.timeseries)) {
        setAdPerformanceData(
          adEventsData.timeseries.map((r: any) => ({
            date: formatDate(r.date || r.day),
            impressions: r?.impressions ?? r?.total ?? 0,
            clicks: r?.clicks ?? 0,
          }))
        );
      } else {
        setAdPerformanceData([]);
      }

      // --- Performance KPIs ---
      const convRate = expandedData?.conversion_rate ?? expandedData?.signup_to_player_rate ?? 0;
      const compRate = summaryData?.completion_rate ?? 0;

      let retRate = 0;
      if (engagementData) {
        if (Array.isArray(engagementData) && engagementData.length > 0) {
          const last = engagementData[engagementData.length - 1];
          retRate = last?.retention_rate ?? last?.day7_retention ?? last?.retention_7d ?? 0;
        } else if (!Array.isArray(engagementData)) {
          const eng = engagementData as any;
          retRate = eng?.retention_rate ?? eng?.day7_retention ?? eng?.retention_7d ?? 0;
        }
      }

      let fillRate = 0;
      if (adEventsData) {
        const totalRequests = adEventsData?.total_requests ?? adEventsData?.ad_requests ?? 0;
        const totalFilled = adEventsData?.total_filled ?? adEventsData?.ads_filled ?? adEventsData?.total_impressions ?? 0;
        fillRate = totalRequests > 0 ? totalFilled / totalRequests : 0;
      }

      setPerformanceKPIs({
        conversionRate: typeof convRate === 'number' ? convRate : 0,
        completionRate: typeof compRate === 'number' ? compRate : 0,
        retentionRate: typeof retRate === 'number' ? retRate : 0,
        adFillRate: typeof fillRate === 'number' ? fillRate : 0,
      });

      // --- Derive insights from real data ---
      const derivedInsights: Insight[] = [];

      const userGrowth = summaryData?.user_growth_percentage ?? 0;
      if (userGrowth > 5) {
        derivedInsights.push({
          icon: 'good',
          title: 'User Growth Strong',
          description: `New user growth is ${userGrowth.toFixed(1)}% over the selected period`,
        });
      } else if (userGrowth > 0) {
        derivedInsights.push({
          icon: 'info',
          title: 'Steady User Growth',
          description: `User growth at ${userGrowth.toFixed(1)}% - consider acquisition campaigns`,
        });
      } else {
        derivedInsights.push({
          icon: 'warning',
          title: 'User Growth Declining',
          description: `User growth at ${userGrowth.toFixed(1)}% - needs attention`,
        });
      }

      const revenueGrowth = summaryData?.revenue_growth_percentage ?? 0;
      if (revenueGrowth > 0) {
        derivedInsights.push({
          icon: 'good',
          title: 'Revenue Trending Up',
          description: `Revenue grew ${revenueGrowth.toFixed(1)}% vs previous period`,
        });
      } else {
        derivedInsights.push({
          icon: 'warning',
          title: 'Revenue Needs Attention',
          description: `Revenue changed ${revenueGrowth.toFixed(1)}% vs previous period`,
        });
      }

      if (totalProtocol > 0) {
        const demoPct = ((demoTotal / totalProtocol) * 100).toFixed(0);
        const multiplyPct = ((multiplyTotal / totalProtocol) * 100).toFixed(0);
        const dominant = demoTotal >= multiplyTotal ? 'Demo' : 'Multiply';
        derivedInsights.push({
          icon: 'info',
          title: `${dominant} Games Lead`,
          description: `Demo ${demoPct}% vs Multiply ${multiplyPct}% of all sessions`,
        });
      }

      const margin = summaryData?.platform_margin ?? 0;
      const revenue = summaryData?.total_revenue ?? 0;
      if (revenue > 0 && margin > 0) {
        const marginPct = ((margin / revenue) * 100).toFixed(1);
        derivedInsights.push({
          icon: margin / revenue > 0.05 ? 'good' : 'warning',
          title: margin / revenue > 0.05 ? 'Healthy Margins' : 'Thin Margins',
          description: `Platform margin at ${marginPct}% of total revenue`,
        });
      }

      setInsights(derivedInsights.length > 0 ? derivedInsights : [
        { icon: 'info', title: 'No Insights Yet', description: 'Not enough data to generate insights for this period' }
      ]);

      lastUpdatedRef.current = new Date();
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadAnalytics();
    const interval = setInterval(loadAnalytics, 60000);
    return () => clearInterval(interval);
  }, [loadAnalytics]);

  const formatNumber = (num: number | undefined | null): string => {
    if (num == null) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number | undefined | null): string => {
    if (amount == null) return '₦0';
    return '₦' + formatNumber(amount);
  };

  const formatPercent = (value: number, isDecimal = true): string => {
    const pct = isDecimal ? value * 100 : value;
    return `${pct.toFixed(1)}%`;
  };

  const handleExport = async () => {
    if (!summary) return;

    const rows: string[][] = [['Metric', 'Value']];
    rows.push(['Total Users', String(summary.total_users ?? 0)]);
    rows.push(['Active Users Today', String(summary.active_users_today ?? 0)]);
    rows.push(['Total Games', String(summary.total_games ?? 0)]);
    rows.push(['Total Revenue', String(summary.total_revenue ?? 0)]);
    rows.push(['Total Payouts', String(summary.total_payouts ?? 0)]);
    rows.push(['Platform Margin', String(summary.platform_margin ?? 0)]);
    rows.push(['Completion Rate', formatPercent(summary.completion_rate ?? 0)]);
    rows.push(['Avg Session Duration (min)', String(summary.avg_session_duration ?? 0)]);
    rows.push(['Ad Impressions Today', String(summary.ad_impressions_today ?? 0)]);
    rows.push(['User Growth %', String(summary.user_growth_percentage ?? 0)]);
    rows.push(['Revenue Growth %', String(summary.revenue_growth_percentage ?? 0)]);

    rows.push([]);
    rows.push(['Date', 'Users', 'Sessions']);
    for (const row of timeSeriesData) {
      rows.push([row.date, String(row.users), String(row.sessions)]);
    }

    rows.push([]);
    rows.push(['Date', 'Revenue']);
    for (const row of revenueData) {
      rows.push([row.date, String(row.revenue)]);
    }

    rows.push([]);
    rows.push(['Date', 'Impressions', 'Clicks']);
    for (const row of adPerformanceData) {
      rows.push([row.date, String(row.impressions), String(row.clicks)]);
    }

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${dateRange}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="metrics-page-modern">
        <div className="loading-container">
          <RefreshCw className="spin" size={40} />
          <span>Loading Analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="metrics-page-modern">
      {/* Header */}
      <div className="metrics-header-modern">
        <div className="header-left">
          <h1><BarChart3 size={28} /> Analytics & Metrics</h1>
          <p className="header-subtitle">Real-time platform performance insights</p>
        </div>
        <div className="header-actions">
          <div className="date-range-selector">
            <Calendar size={16} />
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              {DATE_RANGE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button onClick={handleExport} className="export-btn">
            <Download size={18} /> Export CSV
          </button>
          <button onClick={loadAnalytics} className="refresh-btn" disabled={refreshing}>
            <RefreshCw className={refreshing ? 'spin' : ''} size={18} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="realtime-banner" style={{ background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)' }}>
          <div className="realtime-indicator">
            <AlertTriangle size={18} />
            Error
          </div>
          <div className="realtime-stats">
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Real-time Stats */}
      <div className="realtime-banner">
        <div className="realtime-indicator">
          <span className="pulse-dot"></span>
          Live Data
        </div>
        <div className="realtime-stats">
          <div className="rt-stat">
            <Users size={16} />
            <span>{summary?.active_users_today ?? 0} active now</span>
          </div>
          <div className="rt-stat">
            <Gamepad2 size={16} />
            <span>{summary?.active_games_now ?? summary?.completed_games ?? 0} games completed</span>
          </div>
          <div className="rt-stat">
            <Clock size={16} />
            Last updated: {lastUpdatedRef.current.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card primary">
          <div className="kpi-icon"><Users size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{formatNumber(summary?.total_users)}</span>
            <span className="kpi-label">Total Users</span>
            <div className={`kpi-trend ${(summary?.user_growth_percentage ?? 0) >= 0 ? 'up' : 'down'}`}>
              {(summary?.user_growth_percentage ?? 0) >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {(summary?.user_growth_percentage ?? 0).toFixed(1)}% vs last period
            </div>
          </div>
        </div>

        <div className="kpi-card success">
          <div className="kpi-icon"><Activity size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{formatNumber(summary?.active_users_today)}</span>
            <span className="kpi-label">Active Today</span>
            <div className="kpi-trend up">
              <ArrowUpRight size={14} />
              +{summary?.new_users_today ?? 0} new
            </div>
          </div>
        </div>

        <div className="kpi-card info">
          <div className="kpi-icon"><Gamepad2 size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{formatNumber(summary?.total_games)}</span>
            <span className="kpi-label">Protocol Sessions</span>
            <div className="kpi-trend">
              <Target size={14} />
              {formatPercent(summary?.completion_rate ?? 0)} completion
            </div>
          </div>
        </div>

        <div className="kpi-card warning">
          <div className="kpi-icon"><DollarSign size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{formatCurrency(summary?.total_revenue)}</span>
            <span className="kpi-label">Total Revenue</span>
            <div className={`kpi-trend ${(summary?.revenue_growth_percentage ?? 0) >= 0 ? 'up' : 'down'}`}>
              {(summary?.revenue_growth_percentage ?? 0) >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {(summary?.revenue_growth_percentage ?? 0).toFixed(1)}%
            </div>
          </div>
        </div>

        <div className="kpi-card danger">
          <div className="kpi-icon"><TrendingDown size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{formatCurrency(summary?.total_payouts)}</span>
            <span className="kpi-label">Total Payouts</span>
          </div>
        </div>

        <div className="kpi-card purple">
          <div className="kpi-icon"><Award size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{formatCurrency(summary?.platform_margin)}</span>
            <span className="kpi-label">Platform Margin</span>
            <div className="kpi-trend up">
              <PercentIcon size={14} />
              {summary?.total_revenue
                ? `${((summary.platform_margin / summary.total_revenue) * 100).toFixed(1)}% of revenue`
                : 'N/A'}
            </div>
          </div>
        </div>

        <div className="kpi-card teal">
          <div className="kpi-icon"><Timer size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{(summary?.avg_session_duration ?? 0).toFixed(1)}m</span>
            <span className="kpi-label">Avg Session</span>
          </div>
        </div>

        <div className="kpi-card orange">
          <div className="kpi-icon"><Eye size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-value">{formatNumber(summary?.ad_impressions_today)}</span>
            <span className="kpi-label">Media Impressions</span>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="charts-row">
        <div className="chart-card large">
          <div className="chart-header">
            <h3><TrendingUp size={20} /> User & Session Activity</h3>
            <div className="chart-legend">
              <span className="legend-item users"><span></span> Users</span>
              <span className="legend-item games"><span></span> Sessions</span>
            </div>
          </div>
          {timeSeriesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeSeriesData}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#410C6D" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#410C6D" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorGames" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: '#1f2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Area type="monotone" dataKey="users" stroke="#410C6D" fill="url(#colorUsers)" strokeWidth={2} />
                <Area type="monotone" dataKey="sessions" stroke="#22c55e" fill="url(#colorGames)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              No activity data available
            </div>
          )}
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3><Gamepad2 size={20} /> Protocol Type</h3>
          </div>
          {gameModeData.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={gameModeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                >
                  {gameModeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              No game data available
            </div>
          )}
          <div className="pie-legend">
            {gameModeData.map((item, idx) => (
              <div key={idx} className="pie-legend-item">
                <span className="dot" style={{ background: item.color }}></span>
                <span>{item.name}</span>
                <strong>{formatNumber(item.value)}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="charts-row">
        <div className="chart-card">
          <div className="chart-header">
            <h3><DollarSign size={20} /> Revenue Trend</h3>
          </div>
          {revenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={(v) => `₦${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value) => [`₦${(value as number || 0).toLocaleString()}`, 'Revenue']}
                  contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                />
                <Bar dataKey="revenue" fill="#5a1494" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              No revenue data available
            </div>
          )}
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3><Zap size={20} /> Ad Performance</h3>
          </div>
          {adPerformanceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={adPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px', color: '#fff' }} />
                <Line type="monotone" dataKey="impressions" stroke="#f59e0b" strokeWidth={2} dot={false} name="Impressions" />
                <Line type="monotone" dataKey="clicks" stroke="#ec4899" strokeWidth={2} dot={false} name="Clicks" />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              No ad data available
            </div>
          )}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="performance-section">
        <h3><Target size={20} /> Key Performance Indicators</h3>
        <div className="performance-grid">
          <div className="performance-card">
            <div className="perf-header">
              <span>Conversion Rate</span>
              <span className="perf-value">{formatPercent(performanceKPIs.conversionRate, performanceKPIs.conversionRate <= 1)}</span>
            </div>
            <div className="perf-bar">
              <div
                className="perf-fill"
                style={{
                  width: `${Math.min((performanceKPIs.conversionRate <= 1 ? performanceKPIs.conversionRate * 100 : performanceKPIs.conversionRate), 100)}%`,
                  background: '#410C6D',
                }}
              ></div>
            </div>
          </div>
          <div className="performance-card">
            <div className="perf-header">
              <span>Game Completion</span>
              <span className="perf-value">{formatPercent(performanceKPIs.completionRate)}</span>
            </div>
            <div className="perf-bar">
              <div
                className="perf-fill"
                style={{
                  width: `${Math.min(performanceKPIs.completionRate * 100, 100)}%`,
                  background: '#22c55e',
                }}
              ></div>
            </div>
          </div>
          <div className="performance-card">
            <div className="perf-header">
              <span>User Retention</span>
              <span className="perf-value">{formatPercent(performanceKPIs.retentionRate, performanceKPIs.retentionRate <= 1)}</span>
            </div>
            <div className="perf-bar">
              <div
                className="perf-fill"
                style={{
                  width: `${Math.min((performanceKPIs.retentionRate <= 1 ? performanceKPIs.retentionRate * 100 : performanceKPIs.retentionRate), 100)}%`,
                  background: '#f59e0b',
                }}
              ></div>
            </div>
          </div>
          <div className="performance-card">
            <div className="perf-header">
              <span>Ad Fill Rate</span>
              <span className="perf-value">{formatPercent(performanceKPIs.adFillRate, performanceKPIs.adFillRate <= 1)}</span>
            </div>
            <div className="perf-bar">
              <div
                className="perf-fill"
                style={{
                  width: `${Math.min((performanceKPIs.adFillRate <= 1 ? performanceKPIs.adFillRate * 100 : performanceKPIs.adFillRate), 100)}%`,
                  background: '#5a1494',
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="insights-section">
        <h3><MousePointer size={20} /> Quick Insights</h3>
        <div className="insights-grid">
          {insights.map((insight, idx) => (
            <div key={idx} className="insight-card">
              <div className={`insight-icon ${insight.icon}`}>
                {insight.icon === 'good' && <TrendingUp size={24} />}
                {insight.icon === 'warning' && <AlertTriangle size={24} />}
                {insight.icon === 'info' && <Gamepad2 size={24} />}
              </div>
              <div className="insight-content">
                <h4>{insight.title}</h4>
                <p>{insight.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Metrics;
